/**
 * ApStatusTile — high-level state breakdown for the idle Aktionen view.
 *
 * Counts *records* (not operations) by the effective state of their latest
 * operation. Lives at the top of the record list to give a "how many of my
 * suppliers/customers are in which progress state" overview.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconDownload,
} from "@tabler/icons-react"
import clsx from "clsx"
import { useMemo } from "react"
import type { ApStateBreakdown } from "./exportOperationsStore"
import {
  summarizeRecordsForAp,
  useExportOperationsStore,
} from "./exportOperationsStore"

interface ApStatusTileProps {
  apId: string
  recordIds: string[]
  /** Plural record label, e.g. "Zulieferer" / "Kunden". */
  recordLabel: string
}

export function ApStatusTile({
  apId,
  recordIds,
  recordLabel,
}: ApStatusTileProps) {
  const { translate } = useTranslation()
  const operations = useExportOperationsStore((s) => s.operations)
  const breakdown = useMemo(
    () => summarizeRecordsForAp(operations, apId, recordIds),
    [operations, apId, recordIds],
  )

  // Only render when at least one record has been touched — avoid noise on
  // a fresh AP where every counter is zero except `none`.
  const tracked =
    breakdown.due +
    breakdown.waiting +
    breakdown.followUp +
    breakdown.ok +
    breakdown.download
  if (tracked === 0) return null

  return (
    <div className="border-b border-grey4 px-4 py-3">
      <div className="rounded-lg border border-grey4 bg-grey1 px-3 py-2">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-grey5">
          {recordLabel} — {translate("QMS/AP_STATUS_TILE/STAND_LABEL")}
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
          <Counter
            icon={<IconAlertCircle size={13} className="text-red-400" />}
            count={breakdown.due}
            label={translate("QMS/AP_STATUS_TILE/DUE")}
            tone="red"
          />
          <Counter
            icon={<IconAlertTriangle size={13} className="text-orange-400" />}
            count={breakdown.followUp}
            label={translate("QMS/AP_STATUS_TILE/FOLLOW_UP")}
            tone="orange"
          />
          <Counter
            icon={<IconClock size={13} className="text-yellow1" />}
            count={breakdown.waiting}
            label={translate("QMS/AP_STATUS_TILE/WAITING")}
            tone="yellow"
          />
          <Counter
            icon={<IconCheck size={13} className="text-green-400" />}
            count={breakdown.ok}
            label={translate("QMS/AP_STATUS_TILE/COMPLETED")}
            tone="green"
          />
        </div>
        {breakdown.download > 0 && (
          <p className="mt-1.5 flex items-center gap-1 text-[10px] text-grey5">
            <IconDownload size={10} />
            {breakdown.download}{" "}
            {breakdown.download === 1
              ? translate("QMS/AP_STATUS_TILE/RECORD_SINGULAR")
              : translate("QMS/AP_STATUS_TILE/RECORD_PLURAL")}{" "}
            {translate("QMS/AP_STATUS_TILE/PDF_ONLY")}
          </p>
        )}
      </div>
    </div>
  )
}

function Counter({
  icon,
  count,
  label,
  tone,
}: {
  icon: React.ReactNode
  count: number
  label: string
  tone: "red" | "orange" | "yellow" | "green"
}) {
  const dim = count === 0
  return (
    <div
      className={clsx(
        "flex items-center gap-1.5 text-[11px]",
        dim && "opacity-50",
      )}
    >
      {icon}
      <span
        className={clsx(
          "font-semibold tabular-nums",
          dim && "text-grey5",
          !dim && tone === "red" && "text-red-400",
          !dim && tone === "orange" && "text-orange-400",
          !dim && tone === "yellow" && "text-yellow1",
          !dim && tone === "green" && "text-green-400",
        )}
      >
        {count}
      </span>
      <span className="text-grey5">{label}</span>
    </div>
  )
}

export type { ApStateBreakdown }
